// utils/appendice-search.ts — FP6 Appendice v2 Search v2.
//
// Accent-insensitive, ranked, multi-source search over the LOADED client-side
// indices: signs (always bundled) + principles (fetched per query from the BE)
// + sub-section titles (from chapters the student has opened). We deliberately
// keep this CLIENT-SIDE rather than building a Postgres FTS endpoint:
//   - The sign index (386 rows) ships in the bundle already — searching it on
//     the server would mean a round-trip for data the client already holds.
//   - Principle content search already has a BE endpoint (`/api/principles?q=`);
//     Search v2 ranks those hits alongside the local sign/sub-section matches.
//   - No pgvector / FTS migration is in scope for FP6 (the PRD's "Postgres FTS"
//     line is superseded by this lighter, zero-migration approach — justified in
//     the FP6 return notes).
// Ranking favours: exact name match > prefix match > word-boundary match >
// substring; with a small source weight so signs/principles outrank a buried
// sub-section title at equal textual closeness.

import { SIGN_INDEX, type SignIndexEntry } from '../data/signs';

/** Accent-insensitive, lowercase, whitespace-collapsed normaliser. */
export function normalise(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
}

export type AppendiceResultKind = 'sign' | 'principle' | 'subsection';

export interface AppendiceSearchResult {
  kind: AppendiceResultKind;
  /** Stable React key + navigation target id. */
  id: string;
  /** Primary line shown in the result row. */
  title: string;
  /** Secondary context line (category, argomento, figura…). */
  subtitle?: string;
  /** Navigation hint consumed by the screen. */
  nav: SignNav | PrincipleNav | SubsectionNav;
  /** 0..N score; higher is better. */
  score: number;
}

export interface SignNav { type: 'sign'; signId: string }
export interface PrincipleNav { type: 'principle'; argomentoId: string; principleId: string }
export interface SubsectionNav { type: 'subsection'; argomentoId: string; anchor: string }

// Source weights — at equal textual closeness, a sign/principle beats a buried
// sub-section title. Tuned so a strong sub-section match can still outrank a
// weak sign substring.
const SOURCE_WEIGHT: Record<AppendiceResultKind, number> = {
  sign: 1.0,
  principle: 1.0,
  subsection: 0.85,
};

/**
 * Scores how well `haystack` matches `needle` (both pre-normalised).
 * 0 = no match. Higher is closer:
 *   100 exact · 60 prefix · 40 word-boundary · 20 substring.
 */
export function scoreMatch(haystackRaw: string, needleNorm: string): number {
  const hay = normalise(haystackRaw);
  if (!hay || !needleNorm) return 0;
  if (hay === needleNorm) return 100;
  if (hay.startsWith(needleNorm)) return 60;
  // Word-boundary: the needle starts a word inside the haystack.
  if (new RegExp(`(^|\\s)${escapeRegExp(needleNorm)}`).test(hay)) return 40;
  if (hay.includes(needleNorm)) return 20;
  return 0;
}

function escapeRegExp(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Best score across a sign's name + aliases + figura. */
function scoreSign(sign: SignIndexEntry, needleNorm: string): number {
  let best = scoreMatch(sign.name, needleNorm);
  for (const a of sign.aliases) best = Math.max(best, scoreMatch(a, needleNorm) - 5);
  if (sign.figura) best = Math.max(best, scoreMatch(sign.figura, needleNorm));
  return best;
}

/** Searches the bundled sign index. Returns scored, sorted results. */
export function searchSigns(query: string, limit = 12): AppendiceSearchResult[] {
  const needle = normalise(query);
  if (needle.length < 2) return [];
  const out: AppendiceSearchResult[] = [];
  for (const sign of SIGN_INDEX) {
    const raw = scoreSign(sign, needle);
    if (raw <= 0) continue;
    out.push({
      kind: 'sign',
      id: sign.id,
      title: sign.name,
      subtitle: [sign.figura, sign.category].filter(Boolean).join(' · ') || undefined,
      nav: { type: 'sign', signId: sign.id },
      score: raw * SOURCE_WEIGHT.sign,
    });
  }
  return out.sort((a, b) => b.score - a.score).slice(0, limit);
}

export interface SubsectionLite {
  argomentoId: string;
  num: string;     // "1.1" → anchor "sub-1-1"
  title: string;
}

/** Searches a set of already-loaded sub-section titles. */
export function searchSubsections(
  query: string,
  subsections: SubsectionLite[],
  limit = 8,
): AppendiceSearchResult[] {
  const needle = normalise(query);
  if (needle.length < 2) return [];
  const out: AppendiceSearchResult[] = [];
  for (const s of subsections) {
    const raw = scoreMatch(s.title, needle);
    if (raw <= 0) continue;
    out.push({
      kind: 'subsection',
      id: `${s.argomentoId}-${s.num}`,
      title: s.title,
      subtitle: `Teoria · ${s.num}`,
      nav: { type: 'subsection', argomentoId: s.argomentoId, anchor: anchorForSubsection(s.num) },
      score: raw * SOURCE_WEIGHT.subsection,
    });
  }
  return out.sort((a, b) => b.score - a.score).slice(0, limit);
}

/** Stable anchor id for a sub-section ("1.1" → "sub-1-1"). */
export function anchorForSubsection(num: string): string {
  return `sub-${num.replace(/\./g, '-')}`;
}

/** Merges + re-sorts results from multiple sources into one ranked list. */
export function mergeResults(...groups: AppendiceSearchResult[][]): AppendiceSearchResult[] {
  return groups.flat().sort((a, b) => b.score - a.score);
}
